import {
  RadioGroup,
  RadioGroupItemBaseProps,
  RadioGroupRootBaseProps,
} from "@ark-ui/react";
import { Flex } from "@zenchef/styled-system/jsx";
import { Text } from "../typography/Text";
import {
  aromaRadio,
  AromaRadioVariantProps,
} from "@zenchef/styled-system/recipes";
import { FieldProps } from "../../utils/types";
import withTestId from "../../utils/withTestId";
import Icon from "../Icon/Icon";
import { IconName } from "@zenchef/icomoon";
import { cx } from "@zenchef/styled-system/css";
import {
  ElementRef,
  ForwardedRef,
  forwardRef,
  ReactElement,
  RefAttributes,
} from "react";

export interface AromaRadioGroupProps<T extends string>
  extends AromaRadioVariantProps,
    RadioGroupRootBaseProps,
    FieldProps {
  value?: T;
  title?: string;
  items: AromaRadioItemProps<T>[];
  subtitle?: string;
  defaultValue?: T;
  disabled?: boolean;
  onChange?: (value: T) => void;
  wrapperClassName?: string;
}

export interface AromaRadioItemProps<T extends string>
  extends AromaRadioVariantProps,
    RadioGroupItemBaseProps,
    FieldProps {
  value: T;
  label?: string;
  invalid?: boolean;
  errorText?: string;
  disabled?: boolean;
  description?: string;
  iconName?: IconName;
}

const AromaRadioGroup = forwardRef(function _AromaRadioGroup<T extends string>(
  {
    value,
    defaultValue,
    className,
    wrapperClassName,
    onChange,
    items,
    title,
    subtitle,
    disabled,
    ...propsWithVariants
  }: AromaRadioGroupProps<T>,
  ref: ForwardedRef<ElementRef<"div">>,
) {
  const [variants] = aromaRadio.splitVariantProps(propsWithVariants);
  const classes = aromaRadio(variants);

  return (
    <RadioGroup.Root
      ref={ref}
      value={value}
      defaultValue={defaultValue}
      onValueChange={(details) => {
        onChange?.(details.value as T);
      }}
      aria-labelledby="radio-group-title"
      className={cx(classes.groupContainer, className)}
      disabled={disabled}
    >
      {title && (
        <Flex flexDirection="column" marginBottom="margin.4">
          <Text className={classes.title}>{title}</Text>
          {subtitle && <Text className={classes.subtitle}>{subtitle}</Text>}
        </Flex>
      )}
      <Flex className={wrapperClassName} flexDirection="column" gap="gap.4">
        {items?.map((item) => <AromaRadioItem key={item.value} {...item} />)}
      </Flex>
    </RadioGroup.Root>
  );
});

const AromaRadioItem = forwardRef(function _AromaRadioItem<T extends string>(
  {
    label,
    iconName,
    className,
    value,
    invalid,
    errorText,
    disabled,
    description,
    ...propsWithVariants
  }: AromaRadioItemProps<T>,
  ref: ForwardedRef<ElementRef<"label">>,
) {
  const [variants] = aromaRadio.splitVariantProps(propsWithVariants);
  const classes = aromaRadio(variants);

  return (
    <RadioGroup.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={cx(classes.itemContainer, className)}
      invalid={Boolean(invalid)}
      aria-invalid={Boolean(invalid)}
      key={value}
    >
      <RadioGroup.ItemControl className={classes.control}>
        <RadioGroup.Indicator className={classes.indicator} />
      </RadioGroup.ItemControl>
      <RadioGroup.ItemHiddenInput />
      <RadioGroup.ItemText className={classes.label}>
        <Flex gap={4}>
          <Text>{label}</Text>
          {iconName && <Icon name={iconName} className={classes.icon} />}
        </Flex>
        {invalid ? (
          <Flex className={classes.errorContainer}>
            <Icon name="alert-triangle" />
            <Text className={classes.errorText}>{errorText}</Text>
          </Flex>
        ) : (
          <>
            {description && (
              <Text className={classes.description}>{description}</Text>
            )}
          </>
        )}
      </RadioGroup.ItemText>
    </RadioGroup.Item>
  );
});

export const AromaRadio = {
  Group: AromaRadioGroup as <T extends string>(
    props: AromaRadioGroupProps<T> & RefAttributes<ElementRef<"div">>,
  ) => ReactElement,
  Item: withTestId("radio")(
    AromaRadioItem as <T extends string>(
      props: AromaRadioItemProps<T> & RefAttributes<ElementRef<"div">>,
    ) => ReactElement,
  ),
};

export default AromaRadio;
